import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/layout/Header";
import { PluginCard, PluginCardSkeleton } from "../components/plugins/PluginCard";
import { SearchBar } from "../components/ui/SearchBar";
import { Logo } from "../components/ui/Logo";
import { ChevronRightIcon, CopyIcon, CheckIcon, SparklesIcon } from "../components/ui/Icons";
import {
  pluginApi,
  marketplaceApi,
  statsApi,
  type Plugin,
  type PublicMarketplaceSummary,
  type PlatformStats,
} from "../lib/api";
import { useAuth } from "../hooks/useAuth";

// Animated Terminal Component
function AnimatedTerminal() {
  const apiUrl =
    import.meta.env.VITE_API_URL ||
    (import.meta.env.PROD ? window.location.origin : "http://localhost:3001");

  const lines = [
    { type: "input", text: `claude plugin marketplace add ${apiUrl}/api/marketplace.json` },
    { type: "success", text: "Added marketplace: superchargeclaudecode" },
    { type: "input", text: "claude plugin install setup-tailwind@superchargeclaudecode" },
    { type: "info", text: "Installing setup-tailwind..." },
    { type: "file", text: "Added commands/setup-tailwind.md" },
    { type: "success", text: "Plugin installed" },
    { type: "input", text: "/setup-tailwind" },
    { type: "output", text: "Installing tailwindcss postcss autoprefixer..." },
    { type: "file", text: "Created tailwind.config.js" },
    { type: "success", text: "Tailwind CSS configured" },
  ];

  // Animation state
  const [lineIndex, setLineIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [revealed, setRevealed] = useState<(number | null)[]>(lines.map(() => null));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    const currentLine = lines[lineIndex];
    if (!currentLine) return;

    const isInputLine = currentLine.type === "input";

    if (!isInputLine) {
      // Non-input lines appear instantly, then advance after a short pause
      setRevealed((prev) => {
        const next = [...prev];
        next[lineIndex] = currentLine.text.length;
        return next;
      });
      const nextLine = lineIndex + 1;
      if (nextLine < lines.length) {
        timerRef.current = setTimeout(() => {
          setLineIndex(nextLine);
          setCharIndex(0);
        }, 250);
      }
      // All done — stay static, no restart
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }

    // Input lines: type character by character
    if (charIndex === 0) {
      // Mark line as started (reveal 0 chars so cursor appears)
      setRevealed((prev) => {
        const next = [...prev];
        next[lineIndex] = 0;
        return next;
      });
    }

    if (charIndex < currentLine.text.length) {
      // Variable typing speed: base 45ms with natural jitter
      const char = currentLine.text[charIndex];
      const delay =
        char === " "
          ? 60 + Math.random() * 40 // slight pause on spaces
          : char === "@" || char === "/" || char === "."
            ? 80 + Math.random() * 60 // longer on punctuation
            : 30 + Math.random() * 70; // general variance
      timerRef.current = setTimeout(() => {
        setRevealed((prev) => {
          const next = [...prev];
          next[lineIndex] = charIndex + 1;
          return next;
        });
        setCharIndex((c) => c + 1);
      }, delay);
    } else {
      // Line complete — pause then advance, no restart when done
      const nextLine = lineIndex + 1;
      if (nextLine < lines.length) {
        timerRef.current = setTimeout(() => {
          setLineIndex(nextLine);
          setCharIndex(0);
        }, 400);
      }
      // All done — stay static, no restart
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIndex, charIndex]);

  const getLineStyle = (type: string) => {
    switch (type) {
      case "input":
        return "text-secondary";
      case "success":
        return "text-success";
      case "info":
        return "text-blue-400";
      case "file":
        return "text-amber";
      default:
        return "text-muted";
    }
  };

  const getLinePrefix = (type: string) => {
    switch (type) {
      case "input":
        return <span className="shrink-0 mr-2 text-amber">❯</span>;
      case "success":
        return <span className="shrink-0 mr-2 text-success">✓</span>;
      case "file":
        return <span className="shrink-0 mr-2 text-amber">+</span>;
      case "info":
        return <span className="shrink-0 mr-2 text-blue-400">→</span>;
      default:
        return <span className="shrink-0 mr-2 text-muted"> </span>;
    }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-elevated bg-surface/80 shadow-2xl">
      <div className="flex items-center gap-2 border-b border-elevated px-4 py-3">
        <div className="h-3 w-3 rounded-full bg-danger/60" />
        <div className="h-3 w-3 rounded-full bg-warning/60" />
        <div className="h-3 w-3 rounded-full bg-success/60" />
        <span className="ml-2 font-mono text-xs text-muted">claude code</span>
      </div>
      <div className="p-5 font-mono text-sm" style={{ minHeight: "18rem" }}>
        <div className="space-y-1">
          {lines.map((line, i) => {
            const chars = revealed[i];
            if (chars === null) return null;
            const text = line.text.slice(0, chars);
            const isActive = i === lineIndex && line.type === "input" && chars < line.text.length;
            return (
              <p key={i} className={`${getLineStyle(line.type)} flex items-start`}>
                {getLinePrefix(line.type)}
                <span className="break-all">
                  {text}
                  {isActive && (
                    <span className="ml-px inline-block h-[1em] w-[2px] animate-pulse bg-current align-middle" />
                  )}
                </span>
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
}

type SortTab = "trending" | "featured" | "newest" | "popular";

// Helper to map Plugin to PluginCard props
function mapPluginToCardProps(plugin: Plugin) {
  return {
    id: plugin.id,
    slug: plugin.slug,
    name: plugin.name,
    description: plugin.shortDesc || plugin.description,
    author: plugin.authorName || plugin.author.name,
    tags: plugin.tags,
    downloads: plugin._count?.downloads || 0,
    stars: plugin._count?.favorites || 0,
    version: plugin.versions[0]?.version,
    featured: plugin.featured,
    files: plugin.files.map((f) => ({
      id: f.id,
      fileType: f.fileType,
      displayName: f.displayName,
      fileName: f.fileName,
      description: f.description,
    })),
    pricingModel: plugin.pricingModel,
    price: plugin.price,
  };
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<SortTab>("trending");
  const [plugins, setPlugins] = useState<Plugin[]>([]);
  const [marketplaces, setMarketplaces] = useState<PublicMarketplaceSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");

  // Stats bar
  const [stats, setStats] = useState<PlatformStats | null>(null);

  // New this week
  const [newPlugins, setNewPlugins] = useState<Plugin[]>([]);

  // Fetch published plugins, marketplaces, and user favorites
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [pluginData, marketplaceData] = await Promise.all([
          pluginApi.getPublishedPlugins(),
          marketplaceApi.getAllPublicMarketplaces(),
        ]);
        setPlugins(pluginData);
        setMarketplaces(marketplaceData);
        setError(null);

        // Fetch user favorites if authenticated
        if (isAuthenticated) {
          try {
            const userFavorites = await pluginApi.getUserFavorites();
            setFavorites(new Set(userFavorites));
          } catch {
            // Ignore favorites fetch errors
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load plugins");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated]);

  // Fetch stats independently
  useEffect(() => {
    statsApi
      .getStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  // Fetch new-this-week independently
  useEffect(() => {
    pluginApi
      .getNewThisWeek()
      .then(setNewPlugins)
      .catch(() => setNewPlugins([]));
  }, []);

  const handleStar = async (pluginId: string) => {
    if (!isAuthenticated) return;

    try {
      const result = await pluginApi.toggleFavorite(pluginId);
      setFavorites((prev) => {
        const next = new Set(prev);
        if (result.favorited) {
          next.add(pluginId);
        } else {
          next.delete(pluginId);
        }
        return next;
      });

      // Update the plugin's favorite count in local state
      setPlugins((prev) =>
        prev.map((p) =>
          p.id === pluginId ? { ...p, _count: { ...p._count, favorites: result.count } } : p
        )
      );
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
    }
  };

  // Filter/sort plugins based on active tab and search query
  const displayedPlugins = (() => {
    let filtered = [...plugins];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.shortDesc?.toLowerCase().includes(query) ||
          p.tags?.some((tag) => tag.toLowerCase().includes(query)) ||
          p.authorName?.toLowerCase().includes(query) ||
          p.files?.some(
            (f) =>
              f.displayName?.toLowerCase().includes(query) ||
              f.fileName?.toLowerCase().includes(query)
          )
      );
    }

    // Apply tab filter/sort
    switch (activeTab) {
      case "featured":
        filtered = filtered.filter((p) => p.featured);
        break;
      case "newest":
        filtered = filtered.sort(
          (a, b) =>
            new Date(b.approvedAt || b.createdAt).getTime() -
            new Date(a.approvedAt || a.createdAt).getTime()
        );
        break;
      case "popular":
        filtered = filtered.sort((a, b) => (b._count?.downloads || 0) - (a._count?.downloads || 0));
        break;
      case "trending":
      default:
        // For trending, combine recency with downloads
        filtered = filtered.sort((a, b) => {
          const aScore =
            (a._count?.downloads || 0) +
            new Date(a.approvedAt || a.createdAt).getTime() / 1000000000;
          const bScore =
            (b._count?.downloads || 0) +
            new Date(b.approvedAt || b.createdAt).getTime() / 1000000000;
          return bScore - aScore;
        });
        break;
    }
    return filtered;
  })();

  const apiUrl =
    import.meta.env.VITE_API_URL ||
    (import.meta.env.PROD ? window.location.origin : "http://localhost:3001");
  const marketplaceCommand = `claude plugin marketplace add ${apiUrl}/api/marketplace.json`;

  const copyCommand = () => {
    navigator.clipboard.writeText(marketplaceCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  return (
    <div className="min-h-screen bg-base overflow-x-hidden">
      <Header />

      {/* Hero Section */}
      <section className="pt-16">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            {/* Left side - Text content */}
            <div className="text-center lg:text-left overflow-hidden">
              {/* Headline */}
              <h1 className="mb-6 font-display text-4xl font-bold leading-tight tracking-tight text-secondary md:text-5xl lg:text-6xl">
                The <span className="text-amber">Claude Code</span> Marketplace Platform
              </h1>

              <p className="mx-auto max-w-2xl text-lg leading-relaxed text-muted md:text-xl lg:mx-0">
                Curate skill sets for your team, publish to the community, or discover featured
                collections — all with a single install command.
              </p>

              {/* Feature Points */}
              <div className="mt-8 space-y-3">
                {[
                  {
                    title: "Discover featured marketplaces",
                    desc: "Browse hand-curated skill collections built for every workflow",
                  },
                  {
                    title: "Create your own skill set",
                    desc: "Curate any plugins into a named collection and share via one URL",
                  },
                  {
                    title: "Built for teams",
                    desc: "One command installs your entire team's toolkit — consistent, versioned, shareable",
                  },
                ].map((feature) => (
                  <div key={feature.title} className="flex items-start gap-3 text-left">
                    <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber/20 text-amber text-xs">
                      ✓
                    </span>
                    <div>
                      <p className="font-medium text-secondary">{feature.title}</p>
                      <p className="text-sm text-muted">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <a href="#marketplaces" className="btn-primary text-base">
                  Browse Marketplaces
                  <ChevronRightIcon size={18} />
                </a>
                <Link
                  to={isAuthenticated ? "/dashboard/marketplaces" : "/signup"}
                  className="btn-secondary"
                >
                  <SparklesIcon size={16} className="text-amber" />
                  Create Your Marketplace
                </Link>
              </div>

              <p className="mt-4 font-mono text-xs text-muted">
                New to Claude Code?{" "}
                <Link to="/easy" className="text-amber hover:text-amber-hover">
                  Start here →
                </Link>
              </p>
            </div>

            {/* Right side - Animated terminal */}
            <div className="hidden lg:block">
              <AnimatedTerminal />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-t border-surface bg-surface/30">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6">
            {[
              {
                value: stats ? formatCount(stats.marketplaceCount) : "...",
                label: "Total Marketplaces",
              },
              {
                value: stats ? formatCount(stats.downloadCount) : "...",
                label: "Total Downloads",
              },
              {
                value: stats ? formatCount(stats.pluginCount) : "...",
                label: "Total Skills",
              },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-display text-2xl font-bold text-amber md:text-3xl">
                  {stat.value}
                </p>
                <p className="mt-1 font-mono text-xs text-muted">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Marketplaces Section */}
      <section id="marketplaces" className="relative border-t border-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex items-end justify-between">
            <div>
              <h2 className="font-display text-2xl font-bold text-primary md:text-3xl">
                Featured Marketplaces
              </h2>
              <p className="mt-3 text-muted">
                Hand-picked skill collections for every workflow and team
              </p>
            </div>
            <Link
              to="/browse/marketplaces"
              className="hidden items-center gap-1 font-mono text-sm text-amber transition-colors hover:text-amber-hover sm:flex"
            >
              View all marketplaces
              <ChevronRightIcon size={14} />
            </Link>
          </div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {(isLoading
              ? Array.from({ length: 6 }, (_, i) => ({
                  id: `skeleton-${i}`,
                  slug: "",
                  name: "",
                  description: null,
                  pluginCount: 0,
                  owner: { id: "", name: "", avatarUrl: null },
                  createdAt: "",
                  updatedAt: "",
                }))
              : marketplaces
            ).map((m) =>
              isLoading ? (
                <div key={m.id} className="card-glow animate-pulse p-6">
                  <div className="mb-4 flex items-center gap-4">
                    <div className="h-14 w-14 rounded-xl bg-elevated/50" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-28 rounded bg-elevated/50" />
                      <div className="h-3 w-16 rounded bg-elevated/50" />
                    </div>
                  </div>
                  <div className="mb-2 h-3 w-full rounded bg-elevated/50" />
                  <div className="h-3 w-3/4 rounded bg-elevated/50" />
                </div>
              ) : (
                <Link
                  key={m.id}
                  to={`/m/${m.slug}`}
                  className="card-glow group flex flex-col gap-4 p-6 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-amber/20 text-amber font-display text-2xl font-bold">
                      {m.name.charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-display text-lg font-bold text-primary transition-colors group-hover:text-amber">
                        {m.name}
                      </h3>
                      <p className="font-mono text-xs text-muted">
                        by {(m as PublicMarketplaceSummary).owner?.name || "community"}
                      </p>
                    </div>
                    <ChevronRightIcon
                      size={16}
                      className="mt-1 shrink-0 text-muted opacity-0 transition-opacity group-hover:opacity-100"
                    />
                  </div>
                  <p className="line-clamp-2 text-sm text-muted">
                    {(m as PublicMarketplaceSummary).description || "Curated skill collection"}
                  </p>
                  <p className="font-mono text-xs text-amber/70">
                    {(m as PublicMarketplaceSummary).pluginCount} skill
                    {(m as PublicMarketplaceSummary).pluginCount !== 1 ? "s" : ""}
                  </p>
                </Link>
              )
            )}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/browse/marketplaces"
              className="inline-flex items-center gap-1 font-mono text-sm text-amber transition-colors hover:text-amber-hover"
            >
              View all marketplaces
              <ChevronRightIcon size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Create Your Own Marketplace */}
      <section className="relative border-t border-surface py-16">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute right-0 top-0 h-[400px] w-[400px] -translate-y-1/2 translate-x-1/2 rounded-full bg-purple-500/5 blur-[120px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="font-display text-2xl font-bold text-primary md:text-3xl">
              Create Your Own Marketplace
            </h2>
            <p className="mt-3 text-muted">Your team's perfect toolkit is one URL away</p>
          </div>

          <div className="grid items-start gap-10 lg:grid-cols-2">
            {/* Left: 3 value-prop cards */}
            <div className="space-y-4">
              {[
                {
                  icon: "📦",
                  title: "Build your skill set",
                  desc: "Curate any plugins from the full catalog into a named collection. Organize by category, pick your favorites, and give your marketplace a home.",
                  accent: "bg-purple-500/10 text-purple-400",
                },
                {
                  icon: "🔗",
                  title: "Share with your team",
                  desc: "One URL. Your whole team installs every skill at once. No more copy-pasting plugin commands — just add the marketplace and you're done.",
                  accent: "bg-amber/10 text-amber",
                },
                {
                  icon: "⚡",
                  title: "Easy API",
                  desc: "Every marketplace is a JSON endpoint. Works natively with the Claude Code CLI — just run the add command and your team is set up.",
                  accent: "bg-blue-500/10 text-blue-400",
                },
              ].map((card) => (
                <div key={card.title} className="card-glow flex items-start gap-4 p-5">
                  <div
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg ${card.accent}`}
                  >
                    {card.icon}
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-primary">{card.title}</h3>
                    <p className="mt-1 text-sm text-muted">{card.desc}</p>
                  </div>
                </div>
              ))}

              <div className="pt-2">
                <Link
                  to={isAuthenticated ? "/dashboard/marketplaces" : "/signup"}
                  className="btn-primary text-base"
                >
                  {isAuthenticated ? "Go to My Marketplaces" : "Get Started Free"}
                  <ChevronRightIcon size={18} />
                </Link>
              </div>
            </div>

            {/* Right: Mock UI */}
            <div className="hidden lg:block">
              <div className="rounded-xl border border-elevated bg-surface/50 p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-purple-500/20 text-purple-400 font-display text-xl font-bold">
                    M
                  </div>
                  <div>
                    <p className="font-display font-bold text-primary">My Team's Toolkit</p>
                    <p className="font-mono text-xs text-muted">12 skills · shared with team</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    { cat: "Frontend", count: 4 },
                    { cat: "Testing", count: 5 },
                    { cat: "DevOps", count: 3 },
                  ].map(({ cat, count }) => (
                    <div
                      key={cat}
                      className="flex items-center justify-between rounded-lg bg-base/50 px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <span className="rounded-full bg-amber/10 px-2 py-0.5 text-xs text-amber">
                          {cat}
                        </span>
                      </div>
                      <span className="font-mono text-xs text-muted">{count} skills</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 space-y-2">
                  <p className="font-mono text-xs text-muted">Install everything at once:</p>
                  <code className="block rounded-lg bg-base/80 px-3 py-2 font-mono text-xs text-secondary ring-1 ring-elevated break-all">
                    claude plugin marketplace add .../my-teams-toolkit/marketplace.json
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Teams Section */}
      <section className="relative border-t border-surface py-16">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-0 top-1/2 h-[350px] w-[350px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-success/5 blur-[100px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            {/* Left: headline + bullet points */}
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-success/10 px-3 py-1 font-mono text-xs text-success">
                <span className="h-1.5 w-1.5 rounded-full bg-success" />
                Built for teams
              </div>
              <h2 className="font-display text-2xl font-bold text-primary md:text-3xl">
                Perfect for teams
              </h2>
              <p className="mt-4 text-lg text-muted">
                Stop sending Slack messages with plugin commands. Your team installs everything from
                one URL — and it stays in sync.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Consistent tooling across every engineer",
                  "Version-controlled skill sets",
                  "Share in seconds via a single URL",
                  "Private or public — your call",
                ].map((point) => (
                  <li key={point} className="flex items-center gap-3">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success/20 text-success text-xs">
                      ✓
                    </span>
                    <span className="text-sm text-secondary">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: big code block */}
            <div className="rounded-xl border border-elevated bg-surface/50 p-6">
              <p className="mb-3 font-mono text-xs text-muted">
                Drop this in your team's onboarding doc:
              </p>
              <div className="rounded-xl border border-success/20 bg-base/80 p-5">
                <div className="flex items-center gap-2 border-b border-elevated pb-3 mb-4">
                  <div className="h-2.5 w-2.5 rounded-full bg-danger/60" />
                  <div className="h-2.5 w-2.5 rounded-full bg-warning/60" />
                  <div className="h-2.5 w-2.5 rounded-full bg-success/60" />
                  <span className="ml-2 font-mono text-xs text-muted">onboarding.sh</span>
                </div>
                <div
                  className="font-mono text-sm leading-relaxed break-all"
                  onClick={copyCommand}
                  style={{ cursor: "pointer" }}
                >
                  <span className="text-amber">❯ </span>
                  <span className="text-secondary">{marketplaceCommand}</span>
                </div>
                <p className="mt-3 font-mono text-xs text-success/70">
                  ✓ Everyone on your team now has the same skills
                </p>
              </div>
              <button
                onClick={copyCommand}
                className="mt-4 flex items-center gap-2 rounded-lg bg-success/10 px-4 py-2 font-mono text-xs text-success transition-colors hover:bg-success/20"
              >
                {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
                {copied ? "Copied!" : "Copy command"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Browse Skills Section */}
      <section id="plugins" className="relative border-t border-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="font-display text-2xl font-bold text-primary md:text-3xl">
              Explore Individual Skills
            </h2>
            <p className="mt-3 text-muted">
              Browse every published skill — search, filter, and add to your marketplace
            </p>
          </div>

          {/* Search Bar */}
          <div className="mx-auto w-full max-w-2xl overflow-hidden">
            <SearchBar
              size="large"
              placeholder="Search skills, commands, agents..."
              onSearch={handleSearch}
              liveSearch
            />
          </div>

          {/* Tabs */}
          <div className="-mx-4 mt-10 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <div className="flex items-center justify-start gap-2 sm:justify-center">
              {[
                { id: "trending" as SortTab, label: "Trending" },
                { id: "featured" as SortTab, label: "Featured" },
                { id: "newest" as SortTab, label: "Newest" },
                { id: "popular" as SortTab, label: "Most Popular" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`shrink-0 rounded-lg px-4 py-2 font-mono text-sm transition-colors ${
                    activeTab === tab.id
                      ? "bg-amber text-base"
                      : "text-muted hover:bg-elevated hover:text-secondary"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Plugin Grid - Using columns for masonry-style layout */}
          <div className="mt-10">
            {isLoading ? (
              // Show skeleton loaders while loading
              <div className="columns-1 gap-6 md:columns-2 lg:columns-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="mb-6 break-inside-avoid">
                    <PluginCardSkeleton />
                  </div>
                ))}
              </div>
            ) : error ? (
              // Show error state
              <div className="py-12 text-center">
                <p className="text-muted">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="mt-4 rounded-lg bg-amber px-4 py-2 text-sm font-medium text-base hover:bg-amber-hover"
                >
                  Try Again
                </button>
              </div>
            ) : displayedPlugins.length === 0 ? (
              // Show empty state
              <div className="py-12 text-center">
                <p className="text-lg text-secondary">No skills found</p>
                <p className="mt-2 text-muted">
                  {activeTab === "featured"
                    ? "No featured skills yet."
                    : "Be the first to publish a skill!"}
                </p>
                <Link
                  to="/dashboard"
                  className="mt-4 inline-block rounded-lg bg-amber px-4 py-2 text-sm font-medium text-base hover:bg-amber-hover"
                >
                  Create a Skill
                </Link>
              </div>
            ) : (
              // Show plugins in masonry columns
              <div className="columns-1 gap-6 md:columns-2 lg:columns-3">
                {displayedPlugins.map((plugin) => (
                  <div key={plugin.id} className="mb-6 break-inside-avoid">
                    <PluginCard
                      {...mapPluginToCardProps(plugin)}
                      starred={favorites.has(plugin.id)}
                      onStar={handleStar}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Show count or loading more indicator */}
          {!isLoading && !error && displayedPlugins.length > 0 && (
            <div className="mt-10 flex justify-center">
              <p className="font-mono text-sm text-muted">
                Showing {displayedPlugins.length} skill{displayedPlugins.length !== 1 ? "s" : ""}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* New This Week */}
      {newPlugins.length >= 3 && (
        <section className="border-t border-surface py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h2 className="font-display text-2xl font-bold text-primary md:text-3xl">
                  New This Week
                </h2>
                <p className="mt-1 text-sm text-muted">Fresh skills approved in the last 7 days</p>
              </div>
              <Link
                to="/browse?sort=newest"
                className="hidden items-center gap-1 font-mono text-sm text-amber transition-colors hover:text-amber-hover sm:flex"
              >
                View all
                <ChevronRightIcon size={14} />
              </Link>
            </div>
            {/* Horizontal scroll row */}
            <div className="-mx-4 overflow-x-auto px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
              <div className="flex gap-4 pb-4" style={{ width: "max-content" }}>
                {newPlugins.map((plugin) => (
                  <div key={plugin.id} className="w-72 shrink-0">
                    <PluginCard
                      {...mapPluginToCardProps(plugin)}
                      starred={favorites.has(plugin.id)}
                      onStar={handleStar}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Getting Started Section */}
      <section id="getting-started" className="border-t border-surface py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 text-center">
            <h2 className="font-display text-2xl font-bold text-primary md:text-3xl">
              Getting Started
            </h2>
            <p className="mt-3 text-muted">
              New to Claude Code? Here's everything you need to get up and running.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                step: "1",
                title: "Install Claude Code",
                description:
                  "Claude Code is Anthropic's official CLI for AI-powered development. Install it with npm to get started.",
                code: "npm install -g @anthropic-ai/claude-code",
              },
              {
                step: "2",
                title: "Add a Marketplace",
                description:
                  "Connect to a featured marketplace — or your team's own — to install all its skills at once.",
                code: `claude plugin marketplace add ${apiUrl}/api/marketplace.json`,
              },
              {
                step: "3",
                title: "Install Skills",
                description: "Browse skills above and install any of them with a single command.",
                code: "claude plugin install <skill-name>@superchargeclaudecode",
              },
            ].map((item) => (
              <div key={item.step} className="card-glow p-6">
                <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-lg bg-amber/20 font-display text-sm font-bold text-amber">
                  {item.step}
                </div>
                <h3 className="mb-2 font-display text-lg font-bold text-primary">{item.title}</h3>
                <p className="mb-4 text-sm text-muted">{item.description}</p>
                <code className="block rounded-lg bg-base/80 px-3 py-2 font-mono text-xs text-secondary ring-1 ring-elevated break-all">
                  {item.code}
                </code>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative border-t border-surface py-24">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber/10 blur-[150px]" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="mb-6 font-display text-3xl font-bold tracking-tight text-primary md:text-4xl">
            Create your team's marketplace
          </h2>
          <p className="mb-10 text-lg text-muted">
            Curate the perfect skill set for your team. One URL. Every engineer set up in seconds.
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to={isAuthenticated ? "/dashboard/marketplaces" : "/signup"}
              className="btn-primary text-base"
            >
              Get Started Free
              <ChevronRightIcon size={18} />
            </Link>
            <Link to="/docs" className="btn-secondary">
              Read the Docs
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-surface py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <Logo />

            <nav className="flex flex-wrap items-center justify-center gap-6 font-mono text-sm text-muted">
              <a href="#plugins" className="hover:text-secondary">
                Explore
              </a>
              <a href="#marketplaces" className="hover:text-secondary">
                Marketplaces
              </a>
              <Link to="/create" className="hover:text-secondary">
                Create
              </Link>
              <Link to="/docs" className="hover:text-secondary">
                Documentation
              </Link>
            </nav>

            <span className="font-mono text-sm text-muted">&copy; 2026</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
